import {Link, useSubmit} from 'react-router';
import {useOrganization} from '~/hooks/useOrganization';
import {CirclePlus, Pencil} from 'lucide-react';
import {useState} from 'react';

import {Button} from '~/components/ui/button';
import {Sheet, SheetContent, SheetTrigger} from '~/components/ui/sheet';
import {ManageProfiles} from '~/pages/RegisterPage/ManageProfiles';
import {SelectFromExistingProfiles} from '~/pages/RegisterPage/SelectFromExistingProfiles';
import {EventDescription} from '~/pages/RegisterPage/EventDescription';
import type {
  OrganizationEvent,
  Organization,
  UserProfile,
  UserProfileEventStatus,
} from '~/types';
import {AlreadyRegistered} from './RegisterPage/AlreadyRegistered';
import {LastRefund} from '../components/LastRefund';
import {AlertUnregister} from './RegisterPage/AlertUnregister';
import {Page} from './Page';
import {EventLogin} from './Login/EventLogin';
import {parseTimestampMs} from '~/utils/parseTimestampMs';

interface Props {
  isLoading: boolean;
  event: OrganizationEvent;
  userProfiles: UserProfile[];
  userProfilesCanBeDeleted: boolean[];
  registeredUsersLookup: Record<number, UserProfileEventStatus>;
  organization: Organization;
  hasCustomDomain: boolean;
  isAuthenticated: boolean;
}

export function RegisterPage({
  isLoading,
  event,
  userProfiles,
  userProfilesCanBeDeleted,
  registeredUsersLookup,
  organization,
  hasCustomDomain,
  isAuthenticated,
}: Props) {
  const {orgSlug} = useOrganization();

  const submit = useSubmit();
  const [addProfilesOpen, setAddProfilesOpen] = useState(false);
  const [selectedUserProfiles, setSelectedUserProfiles] = useState<
    UserProfile[]
  >(userProfiles.filter((profile) => registeredUsersLookup[profile.id]));
  const [userToDelete, setUserToDelete] = useState<UserProfile | null>(null);
  const lastRefundTimestamp = parseTimestampMs(event.lastRefundTimestamp);
  const willRefund = lastRefundTimestamp === null || lastRefundTimestamp > Date.now();

  const isEventClosed = event.closed !== null;

  function handleSubmit() {
    const formData = new FormData();

    formData.append('action', 'registerProfiles');
    formData.append('selectedProfiles', JSON.stringify(selectedUserProfiles));
    formData.append('allProfiles', JSON.stringify(userProfiles));

    submit(formData, {
      method: 'post',
    });
  }

  function addSelectedUserProfile(userProfile: UserProfile) {
    setSelectedUserProfiles((prev) => [...prev, userProfile]);
  }

  function removeSelectedUserProfile(userProfile: UserProfile) {
    setSelectedUserProfiles((prev) =>
      prev.filter((profile) => profile.id !== userProfile.id),
    );
  }

  function handleSelectUserProfile(userProfile: UserProfile) {
    const hasProfile = selectedUserProfiles.find(
      (profile) => profile.id === userProfile.id,
    );

    if (hasProfile) {
      removeSelectedUserProfile(userProfile);
    } else {
      addSelectedUserProfile(userProfile);
    }
  }

  function handleUnregister(userProfile: UserProfile) {
    const formData = new FormData();

    formData.append('action', 'unregisterProfile');
    formData.append('selectedProfile', JSON.stringify(userProfile));

    removeSelectedUserProfile(userProfile);

    submit(formData, {
      method: 'post',
    });
  }

  let addEditButtonMarkup = (
    <>
      <Pencil size={18} /> Add or Edit Person
    </>
  );

  if (userProfiles.length === 0) {
    addEditButtonMarkup = (
      <>
        <CirclePlus size={18} /> Add Registrants
      </>
    );
  }

  const alreadyRegisteredProfiles = userProfiles.filter(
    (profile) =>
      registeredUsersLookup[profile.id] === 'paid' ||
      registeredUsersLookup[profile.id] === 'confirmation_sent',
  );
  const notRegisteredProfiles = isEventClosed
    ? []
    : userProfiles.filter(
      (profile) =>
        registeredUsersLookup[profile.id] !== 'paid' &&
        registeredUsersLookup[profile.id] !== 'confirmation_sent',
    );
  const hasRegisteredProfiles = alreadyRegisteredProfiles.length > 0;

  const isAddingNewRegistration =
    notRegisteredProfiles.length > 0 &&
    notRegisteredProfiles.some((profile) =>
      selectedUserProfiles.includes(profile),
    );

  let registerButtonCopy = 'Register';

  if (isAddingNewRegistration) {
    registerButtonCopy = 'Register';
  } else if (hasRegisteredProfiles) {
    registerButtonCopy = 'Edit Registrations';
  }

  let registrationHelperCopy: string | null = null;

  if (notRegisteredProfiles.length > 0 && hasRegisteredProfiles) {
    registrationHelperCopy =
      'Select the people you want to register, or press Unregister to remove an existing registration.';
  } else if (notRegisteredProfiles.length > 0) {
    registrationHelperCopy = 'Select the people you want to register.';
  } else if (hasRegisteredProfiles) {
    registrationHelperCopy =
      'Press Unregister to remove an existing registration.';
  }

  return (
    <Page>
      <div className="flex w-full flex-col items-center pt-0 p-4 md:pt-4">
        <div className="flex w-full flex-col pb-2 max-w-4xl">
          <div className="text-5xl font-bold">{event.name}</div>
          {event.description && (
            <div className="mt-2">
              <EventDescription
                description={event.description}
                eventName={event.name}
              />
            </div>
          )}
        </div>
        {isAuthenticated ? (
          <>
            <div className="flex flex-col w-full items-center mt-4">
              <div className="flex flex-col gap-2 w-full max-w-3xl">
                <div>
                  <div className="text-2xl">Who do you want to register?</div>
                  {registrationHelperCopy && (
                    <div className="mt-1 text-sm text-muted-foreground">
                      {registrationHelperCopy}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-6 rounded-lg border p-4">
                  {hasRegisteredProfiles && (
                    <div>
                      <div className="mb-2 text-lg">Already registered:</div>
                      <AlreadyRegistered
                        isLoading={isLoading}
                        userProfiles={alreadyRegisteredProfiles}
                        onUnregister={setUserToDelete}
                      />
                      {hasRegisteredProfiles && lastRefundTimestamp && (
                        <LastRefund
                          lastRefundTimestamp={lastRefundTimestamp}
                        />
                      )}
                    </div>
                  )}
                  {!isEventClosed && notRegisteredProfiles.length > 0 && (
                    <div>
                      {alreadyRegisteredProfiles.length > 0 && (
                        <div className="mb-2 text-lg">Not registered:</div>
                      )}
                      <SelectFromExistingProfiles
                        isLoading={isLoading}
                        userProfiles={notRegisteredProfiles}
                        selectedUserProfiles={selectedUserProfiles}
                        onUserProfileSelect={handleSelectUserProfile}
                      />
                    </div>
                  )}
                  {!isEventClosed && (
                    <Sheet
                      open={addProfilesOpen}
                      onOpenChange={setAddProfilesOpen}
                    >
                      <SheetTrigger className="w-full" asChild>
                        <Button
                          variant="outline"
                          className="w-full"
                          disabled={isLoading}
                        >
                          {addEditButtonMarkup}
                        </Button>
                      </SheetTrigger>
                      <SheetContent side="right">
                        <ManageProfiles
                          profiles={userProfiles}
                          userProfilesCanBeDeleted={userProfilesCanBeDeleted}
                          closeSheet={() => setAddProfilesOpen(false)}
                        />
                      </SheetContent>
                    </Sheet>
                  )}
                </div>
              </div>
            </div>
            <div className="flex w-full justify-between max-w-3xl mt-6">
              <Button variant="outline" asChild>
                <Link to={hasCustomDomain ? '/' : `/${orgSlug}`}>Back</Link>
              </Button>
              <Button
                type="submit"
                disabled={isLoading || selectedUserProfiles.length === 0}
                onClick={handleSubmit}
              >
                {registerButtonCopy}
              </Button>
            </div>
          </>
        ) : (
          <div className="w-full max-w-3xl">
            <EventLogin
              event={event}
              organization={organization}
              hasCustomDomain={hasCustomDomain}
              redirectPath={`/event/${event.slug}`}
            />
          </div>
        )}
      </div>
      {isAuthenticated && (
        <AlertUnregister
          userToDelete={userToDelete}
          willRefund={willRefund}
          lastRefundTimestamp={lastRefundTimestamp}
          onCancel={() => setUserToDelete(null)}
          onUnregister={() => {
            handleUnregister(userToDelete!);
            setUserToDelete(null);
          }}
        />
      )}
    </Page>
  );
}
